import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import type { Block as BlockT } from '@shared/types';
import { useSessionStore, applyPredefinedFreeVoiceFallback } from '@/stores/session';
import { carryScriptOnModeSwitch } from '@/lib/freeScriptCarry';
import { getPrimaryVoiceId } from '@/lib/voiceMigration';
import { TTSView } from './TTSView';
import { STSView } from './STSView';
import { FreeAssetHeader } from './FreeAssetHeader';

interface FreeChoiceViewProps {
  /** For custom assets, Generation/index.tsx hands its page-level
   *  `<VersionsBar />` down so it can render UNDER the editable title row
   *  (FreeAssetHeader's `versionsBarSlot`) instead of above it. Predefined
   *  free assets keep the page-level mount and pass nothing. */
  versionsBarSlot?: ReactNode;
}
// VersionsBar is mounted once at the page level in Generation/index.tsx
// (FB3#2 — 2026-04-28), so neither TTSView, STSView, nor this wrapper
// renders one. Keeping it canonical there means the bar stays put when
// a free-choice asset flips between TTS and STS modes.
import styles from './FreeChoiceView.module.css';
import { scriptAttrs } from '@/lib/scriptDetect';
import { ScriptHeader } from './components/ScriptHeader';
import { makeEmptyBlock } from './blockFactory';

const scriptHeaderClasses = {
  row: styles.scriptEditorLabelRow,
  actions: styles.scriptEditorActions,
  button: styles.scriptCopyBtn,
  copied: styles.scriptCopyBtnCopied,
};

/** Wrapper for assetType === 'free' (and custom assets). Shows a pill
 *  switcher above the active sub-view; persists the per-asset choice
 *  in S.freeMode so navigation back keeps the same mode.
 *  Mirrors vanilla legacy/index.html line 2207.
 *
 *  Custom-asset extras (vanilla parity):
 *  - STS-mode editable script box (legacy `#customStsScript`, line ~2474).
 *  - Lock-state overlay greys + click-locks asset content until
 *    prerequisites are met (legacy `updateCustomLockState`, line 4214):
 *      • TTS-mode: needs voiceId
 *      • STS-mode: needs script (non-empty after trim)
 *    Header + script editor sit OUTSIDE the lock wrapper so the talent
 *    can complete setup. Mirrors legacy CSS line 751 (.custom-disabled).
 */
export function FreeChoiceView({ versionsBarSlot }: FreeChoiceViewProps = {}) {
  const currentAssetIdx = useSessionStore((s) => s.currentAssetIdx);
  const freeMode = useSessionStore((s) => s.freeMode);
  const setFreeMode = useSessionStore((s) => s.setFreeMode);
  const customAssets = useSessionStore((s) => s.customAssets);
  const upsertCustomAsset = useSessionStore((s) => s.upsertCustomAsset);
  const assetBlocks = useSessionStore((s) => s.assetBlocks);
  const updateActiveBlocks = useSessionStore((s) => s.updateActiveBlocks);
  // Subscribe to slices individually — `selectAllAssets` returns a fresh
  // array each call so subscribing through it would trigger React's
  // "getSnapshot should be cached" infinite loop guard.
  const configAssets = useSessionStore((s) => s.config?.assets);
  const configVoices = useSessionStore((s) => s.config?.voices);
  const allAssets = useMemo(
    () => [
      ...applyPredefinedFreeVoiceFallback(configAssets, configVoices),
      ...customAssets,
    ],
    [configAssets, configVoices, customAssets],
  );

  const predefinedCount = configAssets?.length ?? 0;
  const customIdx = currentAssetIdx - predefinedCount;
  const isCustom = customIdx >= 0;
  const customAsset = isCustom ? customAssets[customIdx] : null;
  const asset = allAssets[currentAssetIdx];
  const mode: 'tts' | 'sts' = (freeMode[currentAssetIdx] as 'tts' | 'sts') ?? 'tts';

  // Silent migration: assets created before scriptTts/scriptSts existed have
  // their script in the legacy `script` field. Reading via `??` falls back to
  // it — once the talent types into the new editor, persistence happens via
  // the mode-specific field going forward and the legacy `script` is left
  // alone (so deleting from it is impossible — but for custom assets the
  // legacy `script` was always written by the talent anyway, so there's
  // nothing to lose).
  const ttsScript = customAsset
    ? customAsset.scriptTts ?? customAsset.script ?? ''
    : '';
  const stsScript = customAsset
    ? customAsset.scriptSts ?? customAsset.script ?? ''
    : '';

  const onStsScriptChange = useCallback((value: string) => {
    if (!customAsset) return;
    upsertCustomAsset({ ...customAsset, scriptSts: value });
  }, [customAsset, upsertCustomAsset]);

  // Track the last scriptTts value that we successfully synced into block 1
  // so each keystroke can decide whether the auto-fill is still valid. The
  // gate is "block 1's text matches what we last set" — i.e. the talent is
  // editing through the script editor only. As soon as the block text drifts
  // (manual block edit, split, etc.) we silently stop auto-filling, but still
  // persist `scriptTts` so the editor itself keeps working.
  // Per-asset map so navigating to another asset (or flipping back from STS)
  // doesn't re-arm auto-fill against a stale comparison value.
  const prevScriptTtsRef = useRef<Record<number, string>>({});
  // Seed the ref when the active asset changes so the first keystroke after
  // navigation compares against the persisted block text, not an empty string.
  useEffect(() => {
    if (!isCustom) return;
    if (prevScriptTtsRef.current[currentAssetIdx] === undefined) {
      const existing = assetBlocks[currentAssetIdx];
      const block0Text = existing && existing.length > 0 ? existing[0].text : '';
      prevScriptTtsRef.current[currentAssetIdx] = block0Text;
    }
  }, [currentAssetIdx, isCustom, assetBlocks]);

  const onTtsScriptChange = useCallback((value: string) => {
    if (!customAsset) return;
    upsertCustomAsset({ ...customAsset, scriptTts: value });
    // Auto-fill block 1 as long as the talent hasn't manually edited it.
    // Detection: block 1's text equals the value we last synced. As soon as
    // it differs, the talent has touched the block UI directly → stop
    // auto-filling silently (still persist scriptTts above).
    const existing = assetBlocks[currentAssetIdx];
    if (!existing || existing.length === 0) {
      // No blocks yet — TTSView's seed-from-script effect will pick up
      // scriptTts on next render. Track the value we'd expect to see.
      prevScriptTtsRef.current[currentAssetIdx] = value;
      return;
    }
    const block0Text = existing[0].text;
    const lastSynced = prevScriptTtsRef.current[currentAssetIdx];
    // Treat undefined (first call after mount with pre-existing blocks) as
    // "in sync if block matches the OLD scriptTts" — but we don't have that
    // here, so we compare against the post-effect seed (block0 may have been
    // initially set to scriptTts via TTSView's seed). The safest behavior is
    // to allow auto-fill iff the block already matches the previous synced
    // value. If lastSynced is undefined, fall back to comparing against the
    // empty-default heuristic (initial unedited state).
    const inSync = lastSynced !== undefined
      ? block0Text === lastSynced
      : (existing.length === 1 && !block0Text.trim() && existing[0].takes.length === 0 && !existing[0].locked);
    if (!inSync) {
      // User has manually diverged block 1 from the script editor — leave
      // blocks alone going forward. Don't update prevScriptTtsRef so the
      // gate stays closed for this asset.
      return;
    }
    prevScriptTtsRef.current[currentAssetIdx] = value;
    updateActiveBlocks((prev) => {
      if (prev.length === 0) return prev; // TTSView's effect will seed it.
      const next: BlockT = { ...prev[0], text: value };
      return [next, ...prev.slice(1)];
    });
  }, [customAsset, upsertCustomAsset, assetBlocks, currentAssetIdx, updateActiveBlocks]);

  // Mode switch: an empty target script inherits the other mode's script so
  // "TTS didn't work, let me speak it" needs no retyping. Routed through the
  // mode handlers so the TTS side keeps its block-1 auto-fill in sync.
  const setMode = useCallback((next: 'tts' | 'sts') => {
    if (customAsset) {
      const carry = carryScriptOnModeSwitch(next, ttsScript, stsScript);
      if (carry?.field === 'scriptSts') onStsScriptChange(carry.value);
      else if (carry?.field === 'scriptTts') onTtsScriptChange(carry.value);
    }
    setFreeMode(currentAssetIdx, next);
  }, [customAsset, ttsScript, stsScript, onStsScriptChange, onTtsScriptChange, currentAssetIdx, setFreeMode]);

  // Lock condition mirrors vanilla `updateCustomLockState` (legacy line 4214).
  // Per-mode script gates use the mode-specific scriptTts/scriptSts fields
  // (with legacy `script` fallback baked into ttsScript/stsScript above).
  // Title is no longer part of the gate — it's captured at creation time by
  // CreateAssetModal and immutable after, so it's always set when we get here
  // (legacy custom assets that pre-date the modal display "Untitled" and are
  // not blocked from generation; the tool was pre-release at the time, so
  // these shouldn't exist in production sessions).
  //   - Custom TTS: locked while voiceId missing
  //   - Custom STS: locked while script blank OR voiceId missing (a voice is
  //     mandatory for STS too — without it the take can be recorded but
  //     never sent to ElevenLabs, which would feel broken to the talent)
  //   - Predefined free assets: never locked (admin guarantees prerequisites)
  const assetVoiceId = getPrimaryVoiceId(asset);
  const isLocked = isCustom && (
    mode === 'tts'
      ? !assetVoiceId
      : (!stsScript.trim() || !assetVoiceId)
  );

  const switchEl = useMemo(() => (
    <div className={styles.switchRow}>
      <button
        type="button"
        className={[styles.pill, mode === 'tts' && styles.pillActive].filter(Boolean).join(' ')}
        onClick={() => setMode('tts')}
      >
        <i className="ph-bold ph-text-aa" /> Text to Speech
      </button>
      <button
        type="button"
        className={[styles.pill, mode === 'sts' && styles.pillActive].filter(Boolean).join(' ')}
        onClick={() => setMode('sts')}
      >
        <i className="ph-bold ph-microphone" /> Speech to Speech
      </button>
    </div>
  ), [mode, setMode]);

  // Script editor for STS-mode custom assets — vanilla parity (#customStsScript).
  // Renders OUTSIDE the lock wrapper so it stays interactive while STS content
  // is locked (matches legacy CSS line 753).
  const stsScriptEditor = isCustom && mode === 'sts' && (
    <div className={styles.scriptEditorWrap}>
      <label className={styles.scriptLabel} htmlFor="custom-sts-script">Script to read</label>
      <textarea
        id="custom-sts-script"
        className={styles.scriptEditor}
        {...scriptAttrs(stsScript)}
        value={stsScript}
        onChange={(e) => onStsScriptChange(e.target.value)}
        placeholder="Enter your script here to unlock the recording tools below…"
        rows={4}
      />
    </div>
  );

  // Copy/Restore action handlers for the editable TTS script editor. Mirrors
  // the static script box UX that predefined assets get in TTSView
  // (`handleCopyScript` / `handleRestoreScript`, ~lines 1210-1236):
  //   - Copy: copies the current script text to clipboard.
  //   - Restore: replaces all blocks with a single block whose text equals
  //     the script. If any block has content / takes, prompt for confirmation
  //     first. Locked blocks are kept (matches predefined behavior).
  const onRestoreTtsScript = useCallback(() => {
    if (!ttsScript) return;
    const existing = assetBlocks[currentAssetIdx] ?? [];
    const hasWork = existing.some((b) => b.text.trim() || b.takes.length > 0);
    const lockedBlocks = existing.filter((b) => b.locked);
    if (hasWork) {
      const msg = lockedBlocks.length > 0
        ? 'This will delete all unlocked blocks and restore the script in one block. Locked blocks will be kept.'
        : 'This will delete all existing blocks and restore the script in one block.';
      if (!window.confirm(msg)) return;
    }
    const next: BlockT[] = [...lockedBlocks, makeEmptyBlock(ttsScript, assetVoiceId)];
    updateActiveBlocks(() => next);
    // Re-arm auto-fill: the freshly-seeded block matches scriptTts exactly,
    // so further edits in the script editor should keep flowing through.
    prevScriptTtsRef.current[currentAssetIdx] = ttsScript;
  }, [ttsScript, assetVoiceId, assetBlocks, currentAssetIdx, updateActiveBlocks]);

  // Script editor for TTS-mode custom assets — coupled to the first block via
  // `onTtsScriptChange` while the block list is still in its initial state.
  // Once the user splits / types directly into a block, the editor stops
  // touching the block text but still persists `scriptTts` so switching back
  // and forth between TTS and STS keeps independent text per mode.
  const ttsScriptEditor = isCustom && mode === 'tts' && (
    <div className={styles.scriptEditorWrap}>
      <ScriptHeader
        label={<label className={styles.scriptLabel} htmlFor="custom-tts-script">Script</label>}
        copyText={ttsScript}
        classes={scriptHeaderClasses}
        onRestore={onRestoreTtsScript}
        disabled={!ttsScript}
      />
      <textarea
        id="custom-tts-script"
        className={styles.scriptEditor}
        {...scriptAttrs(ttsScript)}
        value={ttsScript}
        onChange={(e) => onTtsScriptChange(e.target.value)}
        placeholder="Type your script here…"
        rows={4}
      />
    </div>
  );

  return (
    <>
      <FreeAssetHeader versionsBarSlot={versionsBarSlot} />
      {switchEl}
      {ttsScriptEditor}
      {stsScriptEditor}
      <div className={isLocked ? styles.lockedContent : undefined}>
        {mode === 'tts' ? <TTSView /> : <STSView />}
      </div>
    </>
  );
}
